import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { I18nProvider } from "@/lib/i18n";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import {
  AdminHome,
  UsersPage,
  StudentsAdminPage,
  AttendanceAdminPage,
  PaymentsAdminPage,
} from "@/pages/AdminPages";
import {
  ParentHome,
  ParentChildrenPage,
  ParentCheckInPage,
  ParentPaymentsPage,
} from "@/pages/ParentPages";
import {
  StaffHome,
  TeacherHome,
  StudentAttendancePage,
} from "@/pages/StaffTeacherPages";
import KioskPage from "@/pages/Kiosk";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import ToyyibpayResult from "@/pages/ToyyibpayResult";
import { AdminTimetablePage, ParentTimetablePage } from "@/pages/TimetablePages";
import ReportsPage from "@/pages/ReportsPage";
import FeesAdminPage from "@/pages/FeesAdminPage";
import EnrollmentAdminPage from "@/pages/EnrollmentAdminPage";
import ParentFeesPage from "@/pages/ParentFeesPage";
import ParentAttendancePage from "@/pages/ParentAttendancePage";
import AdminQrPosterPage from "@/pages/AdminQrPosterPage";
import ClassReportPage from "@/pages/ClassReportPage";
import "@/App.css";

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/${user.role}`} replace />;
}

function Guard({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={`/${user.role}`} replace />;
  return <Layout>{children}</Layout>;
}

function Root() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RoleHome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Admin */}
        <Route path="/admin" element={<Guard roles={["admin"]}><AdminHome /></Guard>} />
        <Route path="/admin/users" element={<Guard roles={["admin"]}><UsersPage /></Guard>} />
        <Route path="/admin/students" element={<Guard roles={["admin"]}><StudentsAdminPage /></Guard>} />
        <Route path="/admin/attendance" element={<Guard roles={["admin"]}><AttendanceAdminPage /></Guard>} />
        <Route path="/admin/timetable" element={<Guard roles={["admin"]}><AdminTimetablePage /></Guard>} />
        <Route path="/admin/enrollment" element={<Guard roles={["admin"]}><EnrollmentAdminPage /></Guard>} />
        <Route path="/admin/fees" element={<Guard roles={["admin"]}><FeesAdminPage /></Guard>} />
        <Route path="/admin/payments" element={<Guard roles={["admin"]}><PaymentsAdminPage /></Guard>} />
        <Route path="/admin/reports" element={<Guard roles={["admin"]}><ReportsPage /></Guard>} />
        <Route path="/admin/qr" element={<Guard roles={["admin"]}><AdminQrPosterPage /></Guard>} />

        {/* Staff */}
        <Route path="/staff" element={<Guard roles={["staff"]}><StaffHome /></Guard>} />
        <Route path="/staff/students" element={<Guard roles={["staff"]}><StudentsAdminPage /></Guard>} />
        <Route path="/staff/attendance" element={<Guard roles={["staff"]}><StudentAttendancePage /></Guard>} />

        {/* Teacher */}
        <Route path="/teacher" element={<Guard roles={["teacher"]}><TeacherHome /></Guard>} />
        <Route path="/teacher/attendance" element={<Guard roles={["teacher"]}><StudentAttendancePage /></Guard>} />
        <Route path="/teacher/reports" element={<Guard roles={["teacher"]}><ClassReportPage /></Guard>} />

        {/* Parent */}
        <Route path="/parent" element={<Guard roles={["parent"]}><ParentHome /></Guard>} />
        <Route path="/parent/children" element={<Guard roles={["parent"]}><ParentChildrenPage /></Guard>} />
        <Route path="/parent/timetable" element={<Guard roles={["parent"]}><ParentTimetablePage /></Guard>} />
        <Route path="/parent/checkin" element={<Guard roles={["parent"]}><ParentCheckInPage /></Guard>} />
        <Route path="/parent/attendance" element={<Guard roles={["parent"]}><ParentAttendancePage /></Guard>} />
        <Route path="/parent/fees" element={<Guard roles={["parent"]}><ParentFeesPage /></Guard>} />
        <Route path="/parent/payments" element={<Guard roles={["parent"]}><ParentPaymentsPage /></Guard>} />

        {/* Kiosk (admin/staff only) */}
        <Route path="/kiosk" element={<Guard roles={["admin", "staff"]}><KioskPage /></Guard>} />

        {/* Payment result */}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/payment/toyyibpay-result" element={<ToyyibpayResult />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <div className="App">
          <Root />
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </I18nProvider>
  );
}
